import {
  GetPublishSyncStatusResponse,
  getRefreshToken,
  GetSyncStatusResponse,
} from '@vgrubs/components';
import { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';

const SOCKET_URL = process.env.NX_API_URL;

type MenuSyncPayload = { list: GetSyncStatusResponse[]; menuId?: number };
type MenuShouldPublishPayload = { shouldPublish: boolean; menuId?: number };

/**
 * `/menu` socket for the sync list and the "needs publish" flag. The room is
 * per location + brand and a brand has several menus, so every event names
 * its `menuId`: sync lists for a menu that is not on screen are dropped, and
 * the publish flag is handed over with its menu so the caller can update the
 * menu list as well as the open menu. An event without `menuId` (older
 * backend) is treated as being about the open menu, and so is every event
 * while no menu is picked (`menuId` null: the brand has a single menu).
 *
 * `menuId` and the callback live in refs: the socket is keyed on the token
 * only and must not reconnect when the user switches menus.
 */
const useMenuSyncSocket = ({
  locationId,
  brandId,
  menuId,
  setSyncStatus,
  onShouldPublish,
}: {
  locationId?: number;
  brandId?: number;
  menuId: number | null;
  setSyncStatus: Dispatch<SetStateAction<GetPublishSyncStatusResponse | null>>;
  onShouldPublish: (menuId: number | null, shouldPublish: boolean) => void;
}) => {
  const refreshToken = getRefreshToken();
  const socketRef = useRef<any>(null);

  const menuIdRef = useRef(menuId);
  menuIdRef.current = menuId;
  const onShouldPublishRef = useRef(onShouldPublish);
  onShouldPublishRef.current = onShouldPublish;

  const isForOpenMenu = (eventMenuId?: number) =>
    menuIdRef.current === null ||
    eventMenuId == null ||
    eventMenuId === menuIdRef.current;

  useEffect(() => {
    if (!refreshToken) return;

    if (socketRef.current && socketRef.current.connected) {
      return;
    }

    const socket = io(SOCKET_URL, {
      path: '/menu',
      transports: ['websocket'],
      auth: {
        authorization: refreshToken,
      },
    });

    socketRef.current = socket;

    socket.on('connection_successful', () => {
      const body = {
        subscribe: true,
        locationId,
        brandId,
      };

      socket.emit('observe-menu-sync', body);
    });

    socket.on('observe-menu-sync', (data) => {});

    socket.on('menu_sync', ({ list, menuId: eventMenuId }: MenuSyncPayload) => {
      if (!isForOpenMenu(eventMenuId)) return;
      setSyncStatus((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          syncList: list,
        };
      });
    });

    socket.on(
      'menu_should_publish',
      ({ shouldPublish, menuId: eventMenuId }: MenuShouldPublishPayload) => {
        onShouldPublishRef.current(eventMenuId ?? null, shouldPublish);
      }
    );

    return () => {
      socket.disconnect();
    };
  }, [refreshToken]);

  return {};
};

export default useMenuSyncSocket;
